/**
 * BindingConflictReview
 *
 * Reusable conflict review panel shown before any binding action
 * (Track→Cycle or Session→Track). Displays each conflict with
 * its current vs. incoming value, and offers "Accept" or "Cancel".
 */

import { AlertTriangle, Lock, ChevronRight } from 'lucide-react';
import type { BindingConflict } from '@/lib/bindingConflictDetector';

interface BindingConflictReviewProps {
  /** What is being bound (shown in the header) */
  bindingLabel: string;
  /** Conflicts detected */
  conflicts: BindingConflict[];
  /** Called when the user accepts and proceeds */
  onAccept: () => void;
  /** Called when the user cancels */
  onCancel: () => void;
  /** Whether the accept action is in progress */
  isAccepting?: boolean;
  /** Error message from the accept action */
  error?: string | null;
  /** Label for the accept button, e.g. "Bind to Cycle" */
  acceptLabel?: string;
}

export function BindingConflictReview({
  bindingLabel,
  conflicts,
  onAccept,
  onCancel,
  isAccepting,
  error,
  acceptLabel = 'Accept & Proceed',
}: BindingConflictReviewProps) {
  const lockConflicts = conflicts.filter((c) => c.severity === 'lock');
  const overrideConflicts = conflicts.filter((c) => c.severity === 'override');

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
        <AlertTriangle className="h-5 w-5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-[var(--mustard)]">
            {conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''} detected
          </p>
          <p className="text-xs text-[var(--mustard)] mt-0.5">
            {bindingLabel} — review the differences below before proceeding. The incoming settings
            will take precedence via the inheritance cascade.
          </p>
        </div>
      </div>

      {/* Lock conflicts first (most severe) */}
      {lockConflicts.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-[var(--coral)] uppercase tracking-wide flex items-center gap-1.5">
            <Lock className="h-3.5 w-3.5" />Full lock
          </p>
          {lockConflicts.map((c) => (
            <ConflictCard key={c.field} conflict={c} />
          ))}
        </div>
      )}

      {/* Override conflicts */}
      {overrideConflicts.length > 0 && (
        <div className="space-y-2">
          {lockConflicts.length > 0 && (
            <p className="text-xs font-semibold text-[var(--mustard)] uppercase tracking-wide">
              Individual overrides
            </p>
          )}
          {overrideConflicts.map((c) => (
            <ConflictCard key={c.field} conflict={c} />
          ))}
        </div>
      )}

      {error && (
        <div className="p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
          disabled={isAccepting}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onAccept}
          disabled={isAccepting}
          className="flex-1 px-4 py-2 text-white bg-[var(--mustard)] hover:bg-[var(--mustard)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5"
        >
          {isAccepting ? 'Processing…' : acceptLabel}
          {!isAccepting && <ChevronRight className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

function ConflictCard({ conflict }: { conflict: BindingConflict }) {
  const isLock = conflict.severity === 'lock';
  return (
    <div
      className={` border p-3 ${
        isLock
          ? 'border-[var(--coral)] bg-[var(--coral-dim)]'
          : 'border-[var(--mustard)] bg-[var(--bg-raised)]'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <span
          className={`text-xs font-semibold uppercase tracking-wide ${
            isLock ? 'text-[var(--coral)]' : 'text-[var(--mustard)]'
          }`}
        >
          {conflict.label}
        </span>
        {isLock && <Lock className="h-3.5 w-3.5 text-[var(--coral)] flex-shrink-0" />}
      </div>

      {!isLock && (
        <div className="flex items-center gap-2 text-xs mb-1.5">
          <span className="px-1.5 py-0.5 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded font-mono">
            {conflict.currentValue}
          </span>
          <ChevronRight className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />
          <span className="px-1.5 py-0.5 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded font-mono font-medium">
            {conflict.incomingValue}
          </span>
        </div>
      )}

      <p className={`text-xs leading-relaxed ${isLock ? 'text-[var(--coral)]' : 'text-[var(--text-ter)]'}`}>
        {conflict.description}
      </p>
    </div>
  );
}
